import { defineStore } from "pinia";
import { useGameStore } from "../gameStore";
import { sortVoteEntries } from "@/utils/functions";

const MAX_MSGS = 300;

function isPlayableRole(player) {
  return player?.role === "player" || player?.role === "saboteur";
}

function playablePlayersFromIds(ids, byId) {
  return (ids || [])
    .map((pid) => byId[pid])
    .filter((p) => p && isPlayableRole(p));
}

function getViewerGroupId(state, gameStore) {

  if (gameStore.isHost) {
    return state.currentGroupId ? Number(state.currentGroupId) : null;
  }

  const viewerPlayer = gameStore.player;
  const isModerator = viewerPlayer?.role === "moderator";

if (isModerator) {
  return null;
}

  return state.myGroupId ? Number(state.myGroupId) : null;
}

// @TODO: optimize this later
function votersForCandidate(byId, groupPlayerIds, candidateKey) {
  const voters = [];

  const isNoSaboteur = candidateKey === "-1";
  const candidateId = isNoSaboteur ? null : Number(candidateKey);

  for (const voterPid of groupPlayerIds) {
    const p = byId[voterPid];
    if (!p || !isPlayableRole(p)) continue;

    const votedRaw = p.votedSaboteurId;

    if (votedRaw == null) continue;

    const votedNum = Number(votedRaw);
    if (Number.isNaN(votedNum)) continue;

    let matched = false;

    if (isNoSaboteur) {
      matched = votedNum === 0;
    } else {
      matched = votedNum === candidateId;
    }

    if (!matched) continue;

    voters.push({
      id: p.id ?? voterPid,
      username: p.username ?? `Player ${voterPid}`,
      avatar:
        p.avatar ??
        `https://api.dicebear.com/7.x/bottts-neutral/svg?radius=20&scale=80&seed=${voterPid}`,
    });
  }

  voters.sort((a, b) => (a.username || "").localeCompare(b.username || ""));
  return voters;
}

function uniqueById(items) {
  return Array.from(
    new Map(
      (items || [])
        .filter(Boolean)
        .map((item) => [Number(item.id), item])
    ).values()
  );
}

function sortMessages(messages) {
  return [...(messages || [])].sort((a, b) => {
    const aTime =
      a.createdAt?.seconds ??
      a.created_at?.seconds ??
      a.createdAt ??
      a.created_at ??
      0;

    const bTime =
      b.createdAt?.seconds ??
      b.created_at?.seconds ??
      b.createdAt ??
      b.created_at ??
      0;

    return Number(aTime) - Number(bTime);
  });
}
export const useGameGroupStore = defineStore("gameGroup", {
  state: () => ({
    groupIds: [],
    groupById: {},
    playersByGroupId: {},
    messagesByGroupId: {},
    preliminaryVotesByGroupId: {},
    saboteurByGroupId: {},
    winnerByGroupId: {},
    saboteurVotesByGroupId: {},
    moderatorGroupIds: [],
    myGroupId: null,
    currentGroupId: null,
  }),

  getters: {
  viewerGroupId(state) {
    const gameStore = useGameStore();
    return getViewerGroupId(state, gameStore);
  },

  groups(state) {
    return state.groupIds.map((id) => state.groupById[id]).filter(Boolean);
  },

  groupsWithSaboteur(state) {
    return state.groupIds.map((id) => ({
      ...state.groupById[id],
      saboteur: state.saboteurByGroupId[id] || {},
    }));
  },

  groupsWithWinner(state) {
    return state.groupIds.map((id) => ({
      ...state.groupById[id],
      winner: state.winnerByGroupId[id] || null,
    }));
  },

  groupsWithPlayers(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};

    return state.groupIds.map((gid) => ({
      ...state.groupById[gid],
      saboteur: state.saboteurByGroupId[gid] || {},
      players: playablePlayersFromIds(state.playersByGroupId[gid], byId),
    }));
  },

  groupsWithMessages(state) {
    return state.groupIds.map((gid) => ({
      ...state.groupById[gid],
      messages: state.messagesByGroupId[gid] || [],
    }));
  },

  groupsWithPrelimVotes(state) {
    const gameStore = useGameStore();
    const allVotes = gameStore.game?.preliminaryVotes ?? [];

    return state.groupIds.map((gid) => ({
      ...state.groupById[gid],
      prelimVotes: allVotes.filter((p) => Number(p.groupId) === Number(gid)),
    }));
  },

  groupsWithTextVotes(state) {
    const gameStore = useGameStore();
    const allTexts = gameStore.game?.texts ?? [];
    const allPlayers = (gameStore.game?.players ?? []).filter(isPlayableRole);

    return state.groupIds.map((gid) => {
      const players = playablePlayersFromIds(
        state.playersByGroupId[gid],
        gameStore.playersById
      );

      const textVotes = allTexts.map((t) => {
        const voters = allPlayers.filter(
          (p) =>
            Number(p.votedTextId) === Number(t.id) &&
            Number(p.groupId) === Number(gid)
        );

        return {
          ...t,
          voters,
          votes: voters.length,
        };
      });

      return {
        ...state.groupById[gid],
        players,
        textVotes,
      };
    });
  },

  groupsWithSortedPlayersByScore(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById;

    return state.groupIds.map((gid) => {
      const players = playablePlayersFromIds(state.playersByGroupId[gid], byId);
      players.sort((a, b) => (a.position || Infinity) - (b.position || Infinity));

      return {
        ...state.groupById[gid],
        players,
        topPlayer: players[0] || null,
      };
    });
  },

  groupsWithSortedSaboteurVotes(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};

    return state.groupIds.map((gid) => {
      const voteMap = state.saboteurVotesByGroupId?.[gid] || null;
      const entries = voteMap ? sortVoteEntries(Object.entries(voteMap)) : [];
      const groupPlayerIds = state.playersByGroupId?.[gid] || [];

      const votes = entries.map(([key, count]) => {
        const voters = votersForCandidate(byId, groupPlayerIds, key);

        if (key === "-1") {
          return {
            id: -1,
            votes: count,
            username: "-- No Saboteur --",
            avatar:
              "https://api.dicebear.com/7.x/bottts-neutral/svg?radius=20&scale=80&seed=no_saboteur",
            voters,
          };
        }

        const pid = Number(key);
        const p = byId[pid];

        return {
          id: pid,
          votes: count,
          username: p?.username ?? `Player ${pid}`,
          avatar:
            p?.avatar ??
            `https://api.dicebear.com/7.x/bottts-neutral/svg?radius=20&scale=80&seed=${pid}`,
          voters,
        };
      });

      return {
        ...state.groupById[gid],
        votes,
      };
    });
  },

  groupsWithSortedSaboteurVotesWithPlayers(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};
    const base = this.groupsWithSortedSaboteurVotes || [];

    return base.map((g) => {
      const gid = Number(g.id);
      const players = playablePlayersFromIds(state.playersByGroupId?.[gid], byId);

      return {
        ...g,
        players,
      };
    });
  },

  groupsWithSaboteurVoteCastedPlayers(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};

    return state.groupIds.map((gid) => {
      const allPlayers = playablePlayersFromIds(state.playersByGroupId[gid], byId);

      const players = allPlayers.map((p) => ({
        id: p.id,
        username: p.username,
        avatar: p.avatar,
        votedAt: p.votedAt ?? null,
      }));

      const votedPlayers = players.filter((p) => p.votedAt);
      const notVotedPlayers = players.filter((p) => !p.votedAt);

      return {
        ...state.groupById[gid],
        totalPlayersCount: allPlayers.length,
        votedPlayers,
        notVotedPlayers,
        votesCastedCount: votedPlayers.length,
        voteNotCastedCount: notVotedPlayers.length,
      };
    });
  },

  groupsWithTextVoteCastedPlayers(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};
    const groupsWithTextVotes = this.groupsWithTextVotes;

    return state.groupIds.map((gid) => {
      const allPlayers = playablePlayersFromIds(state.playersByGroupId[gid], byId);

      const votedPlayers = [];
      const notVotedPlayers = [];

      for (const p of allPlayers) {
        const rec = {
          id: p.id,
          username: p.username,
          avatar: p.avatar,
          votedTextId: p.votedTextId ?? null,
        };

        (rec.votedTextId != null ? votedPlayers : notVotedPlayers).push(rec);
      }

      const groupData = groupsWithTextVotes.find(
        (g) => Number(g.id) === Number(gid)
      );
      const textVotes = groupData?.textVotes || [];

      const votersCount = votedPlayers.length;
      const threshold = Math.max(1, allPlayers.length - 1);

      let maxVotes = 0;
      let maxCount = 0;

      for (const tv of textVotes) {
        const v = tv?.votes ?? 0;
        if (v > maxVotes) {
          maxVotes = v;
          maxCount = 1;
        } else if (v === maxVotes && v > 0) {
          maxCount += 1;
        }
      }

      const consensusReached =
        votersCount >= 1 && maxVotes >= threshold && maxCount === 1;

      return {
        ...state.groupById[gid],
        totalPlayersCount: allPlayers.length,
        votedPlayers,
        notVotedPlayers,
        votesCastedCount: votedPlayers.length,
        voteNotCastedCount: notVotedPlayers.length,
        consensusReached,
      };
    });
  },

  groupsWithPrelimVoteCastedPlayers(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};

    return state.groupIds.map((gid) => {
      const allPlayers = playablePlayersFromIds(state.playersByGroupId[gid], byId);

      const players = allPlayers.map((p) => ({
        id: p.id,
        username: p.username,
        avatar: p.avatar,
        prelimVotedAt: p.prelimVotedAt ?? null,
      }));

      const votedPlayers = players.filter((p) => p.prelimVotedAt);
      const notVotedPlayers = players.filter((p) => !p.prelimVotedAt);

      return {
        ...state.groupById[gid],
        totalPlayersCount: allPlayers.length,
        votedPlayers,
        notVotedPlayers,
        votesCastedCount: votedPlayers.length,
        voteNotCastedCount: notVotedPlayers.length,
      };
    });
  },

  moderatorGroups(state) {
    return (state.moderatorGroupIds || [])
      .map((gid) => state.groupById[Number(gid)])
      .filter(Boolean);
  },

  myGroup(state) {
    const gameStore = useGameStore();
    if (gameStore.player?.role === "moderator") {
      const focusedGroupId =
        this.currentGroupId || state.moderatorGroupIds?.[0];

      if (!focusedGroupId) return null;

      return {
        ...state.groupById[Number(focusedGroupId)],
        id: Number(focusedGroupId),
        players: this.myGroupPlayers,
        messages: this.myGroupMessages,
      };
    }
    const groupId = this.viewerGroupId;
    return groupId ? state.groupById[groupId] || null : null;
  },

  myGroupPlayers(state) {
    const gameStore = useGameStore();
    const byId = gameStore.playersById || {};
    if (gameStore.player?.role === "moderator") {
      const focusedGroupId =
        this.currentGroupId || state.moderatorGroupIds?.[0];
      if (!focusedGroupId) return [];
      return playablePlayersFromIds(
        state.playersByGroupId[Number(focusedGroupId)],
        byId
      );
    }
    const groupId = this.viewerGroupId;
    return groupId
      ? playablePlayersFromIds(state.playersByGroupId[groupId], byId)
      : [];
  },

    myGroupMessages(state) {
      const gameStore = useGameStore();

      if (gameStore.player?.role === "moderator") {
        const focusedGroupId =
          this.currentGroupId || state.moderatorGroupIds?.[0];
        if (!focusedGroupId) return [];
        return sortMessages(
          state.messagesByGroupId[Number(focusedGroupId)] || []
        );
      }

      const groupId = this.viewerGroupId;
      return groupId ? state.messagesByGroupId[groupId] || [] : [];
    },

    myGroupPrelimVotes(state) {
      const gameStore = useGameStore();
      const allVotes = gameStore.game?.preliminaryVotes ?? [];
      if (gameStore.player?.role === "moderator") {
        const focusedGroupId =
          this.currentGroupId || state.moderatorGroupIds?.[0];

        if (!focusedGroupId) return [];

        return allVotes.filter(
          (p) => Number(p.groupId) === Number(focusedGroupId)
        );
      }
      const groupId = this.viewerGroupId;
      return groupId
        ? allVotes.filter((p) => Number(p.groupId) === Number(groupId))
        : [];
    },

    myGroupTextVotes(state) {
      const gameStore = useGameStore();
      const allTexts = gameStore.game?.texts ?? [];
      const allPlayers = (gameStore.game?.players ?? []).filter(isPlayableRole);
      if (gameStore.player?.role === "moderator") {
        const focusedGroupId =
          this.currentGroupId || state.moderatorGroupIds?.[0];

        if (!focusedGroupId) return [];

        return allTexts.map((t) => {
          const voters = allPlayers.filter(
            (p) =>
              Number(p.votedTextId) === Number(t.id) &&
              Number(p.groupId) === Number(focusedGroupId)
          );

          return {
            ...t,
            voters,
            votes: voters.length,
          };
        });
      }
      const groupId = this.viewerGroupId;
      if (!groupId) return [];
      return allTexts.map((t) => {
        const voters = allPlayers.filter(
          (p) =>
            Number(p.votedTextId) === Number(t.id) &&
            Number(p.groupId) === Number(groupId)
        );
        return {
          ...t,
          voters,
          votes: voters.length,
        };
      });
    },

myGroupWithSortedPlayersByScore(state) {
  const gameStore = useGameStore();
  const byId = gameStore.playersById || {};
  const focusedGroupId =
    gameStore.player?.role === "moderator"
      ? this.currentGroupId || state.moderatorGroupIds?.[0]
      : this.viewerGroupId;

  const groupIds = focusedGroupId ? [Number(focusedGroupId)] : [];
  if (!groupIds.length) return null;
  const players = uniqueById(
    groupIds.flatMap((gid) =>
      playablePlayersFromIds(state.playersByGroupId[Number(gid)], byId)
    )
  );
  players.sort((a, b) => (a.position || Infinity) - (b.position || Infinity));
  return {
    id: groupIds.length === 1 ? groupIds[0] : "moderator-combined",
    groupIds,
    players,
    topPlayer: players[0] || null,
  };
},

myGroupWithSaboteurVoteCastedPlayers(state) {
  const gameStore = useGameStore();
  const byId = gameStore.playersById || {};
  const focusedGroupId =
    gameStore.player?.role === "moderator"
      ? this.currentGroupId || state.moderatorGroupIds?.[0]
      : this.viewerGroupId;

  const groupIds = focusedGroupId ? [Number(focusedGroupId)] : [];

  if (!groupIds.length) return null;
  const allPlayers = uniqueById(
    groupIds.flatMap((gid) =>
      playablePlayersFromIds(state.playersByGroupId[Number(gid)], byId)
    )
  );
  const players = allPlayers.map((p) => ({
    id: p.id,
    username: p.username,
    avatar: p.avatar,
    votedAt: p.votedAt ?? null,
  }));
  const votedPlayers = players.filter((p) => p.votedAt);
  const notVotedPlayers = players.filter((p) => !p.votedAt);
  return {
    ...(groupIds.length === 1 ? state.groupById[groupIds[0]] : {}),
    id: groupIds.length === 1 ? groupIds[0] : "moderator-combined",
    groupIds,
    totalPlayersCount: allPlayers.length,
    votedPlayers,
    votesCastedCount: votedPlayers.length,
    notVotedPlayers,
    voteNotCastedCount: notVotedPlayers.length,
  };
},
myGroupWithSortedSaboteurVotes(state) {
  const gameStore = useGameStore();
  const byId = gameStore.playersById || {};
  const focusedGroupId =
    gameStore.player?.role === "moderator"
      ? this.currentGroupId || state.moderatorGroupIds?.[0]
      : this.viewerGroupId;

  const groupIds = focusedGroupId ? [Number(focusedGroupId)] : [];
  if (!groupIds.length) return { votes: [] };
  const combinedVotes = [];
  groupIds.forEach((gid) => {
    const voteMap = state.saboteurVotesByGroupId?.[Number(gid)] || {};
    const entries = sortVoteEntries(Object.entries(voteMap));
    const groupPlayerIds = state.playersByGroupId?.[Number(gid)] || [];
    entries.forEach(([key, count]) => {
      const voters = votersForCandidate(byId, groupPlayerIds, key);
      if (key === "-1") {
        combinedVotes.push({
          id: -1,
          votes: count,
          username: "-- No Saboteur --",
          avatar:
            "https://api.dicebear.com/7.x/bottts-neutral/svg?radius=20&scale=80&seed=no_saboteur",
          voters,
        });
      } else {
        const pid = Number(key);
        const p = byId[pid];
        combinedVotes.push({
          id: pid,
          votes: count,
          username: p?.username ?? `Player ${pid}`,
          avatar:
            p?.avatar ??
            `https://api.dicebear.com/7.x/bottts-neutral/svg?radius=20&scale=80&seed=${pid}`,
          voters,
        });
      }
    });
  });
  return {
    id:
      groupIds.length === 1
        ? groupIds[0]
        : "moderator-combined",
    groupIds,
    votes: combinedVotes,
  };
},

myGroupWithPrelimVoteCastedPlayers(state) {
  const gameStore = useGameStore();
  const byId = gameStore.playersById || {};
  const focusedGroupId =
    gameStore.player?.role === "moderator"
      ? this.currentGroupId || state.moderatorGroupIds?.[0]
      : this.viewerGroupId;

  const groupIds = focusedGroupId ? [Number(focusedGroupId)] : [];
  if (!groupIds.length) return null;
  const allPlayers = uniqueById(
    groupIds.flatMap((gid) =>
      playablePlayersFromIds(state.playersByGroupId[Number(gid)], byId)
    )
  );
  const players = allPlayers.map((p) => ({
    id: p.id,
    username: p.username,
    avatar: p.avatar,
    prelimVotedAt: p.prelimVotedAt ?? null,
  }));
  const votedPlayers = players.filter((p) => p.prelimVotedAt);
  const notVotedPlayers = players.filter((p) => !p.prelimVotedAt);
  return {
    ...(groupIds.length === 1 ? state.groupById[groupIds[0]] : {}),
    id: groupIds.length === 1 ? groupIds[0] : "moderator-combined",
    groupIds,
    totalPlayersCount: allPlayers.length,
    votedPlayers,
    votesCastedCount: votedPlayers.length,
    notVotedPlayers,
    voteNotCastedCount: notVotedPlayers.length,
  };
},
},

  actions: {
    setGroups(groups) {
      const groupById = {};
      const groupIds = [];
      const saboteurByGroupId = {};

      for (const g of groups) {
        const gid = Number(g.id);
        groupById[gid] = { ...g, id: gid, fbDocId: g.fbDocId ?? null };
        groupIds.push(gid);
        saboteurByGroupId[gid] = g.saboteur || {};

        this.playersByGroupId[gid] =
        (g.players || g.playerIds || []).map((p) =>
          typeof p === "object" ? Number(p.id) : Number(p)
        );
        if (!this.messagesByGroupId[gid]) this.messagesByGroupId[gid] = [];
        if (!this.preliminaryVotesByGroupId[gid]) {
          this.preliminaryVotesByGroupId[gid] = [];
        }
        if (!this.saboteurVotesByGroupId[gid]) {
          this.saboteurVotesByGroupId[gid] = {};
        }
      }

      this.groupById = groupById;
      this.groupIds = groupIds;
      this.saboteurByGroupId = saboteurByGroupId;
    },

  setCurrentGroupId(groupId) {
    this.currentGroupId = groupId
      ? Number(groupId)
      : null;
    const gameStore = useGameStore();
    // merged moderator mode
    if (gameStore.player?.role === "moderator") {
      return;
    }
    const gid = this.currentGroupId;
    if (!gid || Number.isNaN(gid)) {
      gameStore.setSaboteur({});
      return;
    }
    gameStore.setSaboteur(
      this.saboteurByGroupId?.[gid] ?? {}
    );
  },
    setGroupSaboteur(groupId, saboteur) {
      const gid = Number(groupId);

      this.saboteurByGroupId = {
        ...this.saboteurByGroupId,
        [gid]: saboteur || {},
      };

      if (!this.groupIds.includes(gid)) {
        this.groupIds = [...this.groupIds, gid];
      }

      if (!this.groupById[gid]) {
        this.groupById = {
          ...this.groupById,
          [gid]: { id: gid },
        };
      }
    },

    setMyGroup(groupId, groupData, fbDocId) {
      const gid = Number(groupId);
      this.myGroupId = gid;

      this.groupIds = [gid];
      this.groupById = {
        [gid]: {
          ...groupData,
          id: gid,
          fbDocId: fbDocId ?? null,
        },
      };

      if (!this.playersByGroupId[gid]) this.playersByGroupId[gid] = [];
      if (!this.messagesByGroupId[gid]) this.messagesByGroupId[gid] = [];
      if (!this.preliminaryVotesByGroupId[gid]) {
        this.preliminaryVotesByGroupId[gid] = [];
      }
      if (!this.saboteurVotesByGroupId[gid]) {
        this.saboteurVotesByGroupId[gid] = {};
      }
    },

    setModeratorGroups(groupIds) {
        this.moderatorGroupIds = Array.isArray(groupIds) ? groupIds.map(id => Number(id)): [];
    },

    setPlayersIndex(indexMap) {
      const next = {};
      for (const [gid, ids] of Object.entries(indexMap)) {
        next[Number(gid)] = [...ids];
      }
      this.playersByGroupId = next;
    },

    appendMessage(groupId, msg) {
      this.appendMessageBatch(groupId, [msg]);
    },

    appendMessageBatch(groupId, msgs) {
      const gid = Number(groupId);
      const existing = this.messagesByGroupId[gid] || [];
      const seen = new Set(existing.map((m) => m.docId));
      const toAdd = [];

      for (const m of msgs) {
        if (!seen.has(m.docId)) {
          seen.add(m.docId);
          toAdd.push(m);
        }
      }

      if (!toAdd.length) return;

      const next = [...existing, ...toAdd].slice(-MAX_MSGS);
      this.messagesByGroupId[gid] = next;
    },

    setMessages(groupId, messages) {
      const gid = Number(groupId);
      this.messagesByGroupId[gid] = (messages || []).slice(-MAX_MSGS);
    },

    setWinners(winnerData) {
      const next = {};
      for (const [gid, winner] of Object.entries(winnerData)) {
        next[Number(gid)] = winner;
      }
      this.winnerByGroupId = next;
    },

    setAllGroupsSaboteurVotes(votesData) {
      const next = {};
      for (const [gid, sabVotes] of Object.entries(votesData)) {
        next[Number(gid)] = sabVotes;
      }
      this.saboteurVotesByGroupId = next;
    },

    setGroupSaboteurVotes(votes, groupId) {
      const gid = Number(groupId);
      const prev = this.saboteurVotesByGroupId[gid] || {};
      const next = votes || {};
      const sameSize = Object.keys(prev).length === Object.keys(next).length;
      const same =
        sameSize && Object.entries(prev).every(([k, v]) => next[k] === v);

      if (!same) {
        this.saboteurVotesByGroupId[gid] = next;
      }
    },
    hydrateGroups(groups = []) {
      const groupById = {};
      const groupIds = [];
      const saboteurByGroupId = {};
      const playersByGroupId = {};

      groups.forEach((g) => {
        const gid = Number(g.id);

        groupIds.push(gid);

        groupById[gid] = {
          id: gid,
          name: g.name,
          fbDocId: g.fbDocId ?? null,
        };

        saboteurByGroupId[gid] = g.saboteur || {};

        playersByGroupId[gid] = (g.players || []).map((p) => Number(p.id));
      });

      this.groupIds = groupIds;
      this.groupById = groupById;
      this.saboteurByGroupId = saboteurByGroupId;
      this.playersByGroupId = playersByGroupId;
    },
    resetAll() {
      this.groupById = {};
      this.groupIds = [];
      this.moderatorGroupIds = [];
      this.playersByGroupId = {};
      this.messagesByGroupId = {};
      this.preliminaryVotesByGroupId = {};
      this.saboteurByGroupId = {};
      this.winnerByGroupId = {};
      this.saboteurVotesByGroupId = {};
      this.myGroupId = null;
      this.currentGroupId = null;
    },
  },
});